import * as THREE from 'three';
import { RendererBaseConfig } from '@visualizer/types';
import useNotificationStore from '@gui/store/notification';
import { getViewer } from 'store/viewerStore';
import { DelayedDesignSchemeLoadingOverlay } from './designSchemeLoading';
import { NotificationType } from '@visualizer/types';

const DESIGN_SCHEME_LOADING_TITLE = 'Computing design scheme...';

export abstract class DesignSchemeRenderer {
    protected meshGroup: THREE.Group;
    protected config: RendererBaseConfig;

    // Loading Message flow:
    private readonly loadingOverlay = new DelayedDesignSchemeLoadingOverlay();
    private readonly generationCompleteCallbacks = new Set<() => void>();

    // Handle potential conflicts between async renderers (e.g. if rendering Run A yields (stops) and in the meanwhile a Run B is started):
    private generationId = 0;
    private runningGenerationId: number | null = null;

    constructor(config: RendererBaseConfig) {
        this.meshGroup = new THREE.Group();
        this.config = this.cloneConfig(config);
    }

    // =====================================
    // CORE FUNCTIONALITIES
    // =====================================

    // Used to sync state with frame change:
    protected cloneConfig<T extends RendererBaseConfig>(config: T): T {
        if (typeof structuredClone === 'function') return structuredClone(config);
        return JSON.parse(JSON.stringify(config));
    }

    // Abstract Methods:
    abstract updateConfig(newConfig: RendererBaseConfig): void;
    abstract generateMesh(): Promise<void> | void;

    // Getters and Setters:
    getMeshGroup(): THREE.Group {
        return this.meshGroup;
    }

    getConfig(): RendererBaseConfig {
        return this.cloneConfig(this.config);
    }

    getVisibility(): boolean {
        return this.meshGroup.visible;
    }

    public isGenerating(): boolean {
        return this.runningGenerationId != null;
    }

    setVisibility(visible: boolean): void {
        this.meshGroup.visible = visible;
    }

    // Functionalities:
    addToScene(): void {
        const scene: THREE.Scene = getViewer().scene;
        scene.add(this.meshGroup);
    }

    clean(): void {
        this.generationId += 1;
        this.generationCompleteCallbacks.clear();
        this.loadingOverlay.end();
        const scene: THREE.Scene = getViewer().scene;
        scene.remove(this.meshGroup);

        const oldVisibility = this.meshGroup.visible;
        this.meshGroup = new THREE.Group();
        this.meshGroup.visible = oldVisibility;
    }

    // =====================================
    // LOADING UTILITIES
    // =====================================

    public onGenerationComplete(callback: () => void): () => void {
        this.generationCompleteCallbacks.add(callback);
        return () => {
            this.generationCompleteCallbacks.delete(callback);
        };
    }

    public generateMeshWithLoading(): void {
        const generationId = ++this.generationId;

        this.loadingOverlay.begin({
            title: DESIGN_SCHEME_LOADING_TITLE,
            description: 'The design scheme is being built.',
        });

        void (async () => {
            let didComplete = false;
            let didFail = false;
            try {
                this.runningGenerationId = generationId;
                await this.yieldToUi();
                if (!this.isGenerationCurrent(generationId)) return;

                await this.generateMesh();
                didComplete = true;
            } catch (error) {
                didFail = true;
                console.error('Failed to generate design scheme mesh:', error);
                useNotificationStore
                    .getState()
                    .showNotification(
                        'The design scheme could not be generated because an unexpected error occurred. Please check the console for details.',
                        NotificationType.Error
                    );
            } finally {
                if (this.runningGenerationId === generationId) {
                    this.runningGenerationId = null;
                }
                if (this.isGenerationCurrent(generationId)) {
                    if (didComplete) {
                        const callbacks = Array.from(this.generationCompleteCallbacks);
                        this.generationCompleteCallbacks.clear();
                        callbacks.forEach((callback) => callback());
                        getViewer().requestRender?.('ui');
                        await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
                        await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
                        this.loadingOverlay.end();
                    } else if (didFail) {
                        this.loadingOverlay.end();
                        getViewer().requestRender?.('ui');
                    }
                }
            }
        })();
    }

    protected isGenerationCurrent(generationId: number): boolean {
        return this.generationId === generationId;
    }

    protected shouldAbortGeneration(): boolean {
        return this.runningGenerationId == null || !this.isGenerationCurrent(this.runningGenerationId);
    }

    // Stops the execution to place the Loading Screen:
    protected async yieldToUi(): Promise<void> {
        await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
    }

    setTransparent(transparent: boolean, opacity: number): void {
        this.meshGroup.children.forEach((obj) => {
            const mesh = obj as THREE.Mesh;
            if (mesh.material instanceof THREE.Material) {
                mesh.material.transparent = transparent;
                (mesh.material as any).opacity = transparent ? opacity : 1;
            }
        });
    }
}
